import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { map, path, prop } from 'ramda'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { GridActionsCellItem } from '@mui/x-data-grid-pro'
import EditIcon from '@mui/icons-material/Edit'

import BooleanStatus from '~/components/BooleanStatus'
import TextOverflow from '~/components/TextOverflow'
import { Table, TableHeader } from '~/components/Table'
import * as ROUTES from '~/constants/routes'
import TableLink from '~/components/Link'
import { MoreListButton } from '~/components/Buttons'

const getDetailPath = guid => {
  return generatePath(ROUTES.ZONE_DETAIL_PATH, { guid })
}

export const ZONE_COLUMNS = [
  {
    width: 400,
    headerName: 'Name',
    field: 'name',
    renderCell: ({ value, row }) => (
      <TableLink to={row.link}>
        <TextOverflow selfTooltip={true}>{value}</TextOverflow>
      </TableLink>
    )
  },
  {
    width: 200,
    headerName: 'Label',
    field: 'label'
  },
  {
    width: 200,
    headerName: 'Warehouse',
    field: 'warehouse'
  },
  {
    width: 200,
    headerName: 'Area',
    field: 'area'
  },
  {
    width: 200,
    headerName: 'Status',
    field: 'status',
    renderCell: ({ value }) => (
      <BooleanStatus value={value} />
    )
  }
]

const getRowsFromResults = map(item => {
  const id = prop('id', item)
  const guid = prop('guid', item)
  const name = <TextOverflow selfTooltip={true}>{prop('name', item)}</TextOverflow>
  const label = prop('label', item)
  const warehouse = path(['warehouse', 'name'], item)
  const area = path(['area', 'name'], item)
  const status = prop('status', item)
  const link = getDetailPath(guid)

  return {
    id,
    guid,
    name,
    label,
    warehouse,
    area,
    status,
    link
  }
})

function ZoneTable ({ list, filter, ordering, onListRefetch }) {
  const [tableRef, setTableRef] = useState(null)
  const { guid } = useParams()
  const navigate = useNavigate()
  const isLoading = prop('isLoading', list)
  const results = prop('results', list)
  const count = prop('count', list)
  const columns = prop('filteredColumns', ordering)

  const createPath = generatePath(ROUTES.ZONE_CREATE_PATH, { warehouseGuid: guid })

  const handleEdit = useCallback(guid => {
    const editPath = generatePath(ROUTES.ZONE_UPDATE_PATH, { guid })
    navigate(editPath)
  }, [navigate])

  const actionColumns = useMemo(() => [
    ...columns,
    {
      width: 50,
      resizable: false,
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => [
        <GridActionsCellItem
          label="Edit"
          onClick={() => handleEdit(row.guid)}
          showInMenu={true}
          icon={<EditIcon />}
        />
      ]
    }
  ], [columns, handleEdit])

  return (
    <>
      <TableHeader
        filter={filter}
        filterOpen={filter.handleOpen}
        orderingOpen={ordering.handleOpen}
        ordering={ordering}
        onCreateClick={() => navigate(createPath)}
      >
        <MoreListButton tableRef={tableRef} />
      </TableHeader>
      <Table
        isLoading={isLoading}
        columns={actionColumns}
        getRowsFromResults={getRowsFromResults}
        results={results}
        primaryKey="guid"
        pinnedColumns={['name']}
        count={count}
        checkboxSelection={true}
        ordering={ordering}
        onRefetch={onListRefetch}
        setTableRef={setTableRef}
      />
    </>
  )
}

ZoneTable.propTypes = {
  list: PropTypes.object.isRequired,
  filter: PropTypes.object.isRequired,
  ordering: PropTypes.object.isRequired,
  onListRefetch: PropTypes.func.isRequired
}

export default ZoneTable
